import {
  INewsAndUpdatesNotifications,
  ISettingsVisibility,
  ISlotNotifications,
  IUserSettings,
  UserSettingKey,
} from 'services/player';

// Mirrors the notification entries of USER_SETTINGS_DEFAULTS in
// services/ms-player/src/API/userSettings.ts. Used to fill in fields an older
// server doesn't send yet.
export const NOTIFICATION_DEFAULTS: Pick<
  IUserSettings,
  | 'notificationChannels'
  | 'eventNotifications'
  | 'leaderboardNotifications'
  | 'newsAndUpdates'
  | 'allSlotsNotifications'
  | 'favoriteSlotsNotifications'
  | 'playlistSlotsNotifications'
> = {
  notificationChannels: { inApp: true, push: true, email: false, sms: true },
  eventNotifications: true,
  leaderboardNotifications: true,
  newsAndUpdates: {
    enabled: true,
    weeklyTopSlots: true,
    weeklyTopPlayers: false,
    appUpdates: false,
    newSlots: false,
  },
  allSlotsNotifications: { enabled: true, all: true, jackpotHit: true, trending: false },
  favoriteSlotsNotifications: { enabled: true, all: true, jackpotHit: true, trending: true },
  playlistSlotsNotifications: { enabled: true, all: true, jackpotHit: true, trending: true },
};

// Everything the Notification Settings page's Reset to Default restores.
export const NOTIFICATION_KEYS: UserSettingKey[] = [
  'notificationsEnabled',
  ...(Object.keys(NOTIFICATION_DEFAULTS) as UserSettingKey[]),
];

export type NotificationGroupKey =
  | 'newsAndUpdates'
  | 'allSlotsNotifications'
  | 'favoriteSlotsNotifications'
  | 'playlistSlotsNotifications';

type GroupValue = INewsAndUpdatesNotifications | ISlotNotifications;

export interface NotificationGroupDefinition {
  key: NotificationGroupKey;
  // URL segment under /settings/notifications/.
  slug: string;
  // Row label on the Notification Settings page.
  label: string;
  title: string;
  description: string;
  // Its entry in SETTINGS_ROW_VISIBILITY.notificationSettings.groups.
  visibilityKey: keyof ISettingsVisibility['notificationSettings']['groups'];
  // The toggles under "Allow notifications"; `field` is also the row's visibility key.
  items: { field: string; label: string }[];
  // Value shown on the Notification Settings row, e.g. "All", "Custom", "Off".
  summary: (value: GroupValue) => string;
}

const SLOT_ITEMS = [
  { field: 'all', label: 'All' },
  { field: 'jackpotHit', label: 'Jackpots hit' },
  { field: 'trending', label: 'Trending' },
];

const summarizeSlots = (value: GroupValue) => {
  const { enabled, all, jackpotHit, trending } = value as ISlotNotifications;
  if (!enabled) return 'Off';
  if (all) return 'All';
  if (jackpotHit && trending) return 'Custom';
  if (jackpotHit) return 'Jackpots';
  if (trending) return 'Trending';
  return 'Off';
};

const slotGroup = (
  key: NotificationGroupKey,
  visibilityKey: NotificationGroupDefinition['visibilityKey'],
  slug: string,
  label: string,
  description: string
): NotificationGroupDefinition => ({
  key,
  visibilityKey,
  slug,
  label,
  title: label,
  description,
  items: SLOT_ITEMS,
  summary: summarizeSlots,
});

// Rows on the Notification Settings page that open a sub page, in display order.
export const NOTIFICATION_GROUPS: NotificationGroupDefinition[] = [
  {
    key: 'newsAndUpdates',
    visibilityKey: 'newsAndUpdates',
    slug: 'news-and-updates',
    label: 'News and Updates',
    title: 'News and updates',
    description: 'Stay up to date on everything new with Slot Check!',
    items: [
      { field: 'weeklyTopSlots', label: 'Weekly Top Slots' },
      { field: 'weeklyTopPlayers', label: 'Weekly Top Players' },
      { field: 'appUpdates', label: 'App Updates' },
      { field: 'newSlots', label: 'New Slots' },
    ],
    summary: (value) => {
      const { enabled, ...topics } = value as INewsAndUpdatesNotifications;
      const flags = Object.values(topics);
      if (!enabled || flags.every((on) => !on)) return 'Off';
      return flags.every(Boolean) ? 'All' : 'Custom';
    },
  },
  slotGroup(
    'allSlotsNotifications',
    'allSlots',
    'all-slots',
    'All Slots',
    'You will be notified when any Slot meet the selected notification criteria.'
  ),
  slotGroup(
    'favoriteSlotsNotifications',
    'favoriteSlots',
    'favorite-slots',
    'Favorite Slots',
    'You will be notified when any of your favorite slots meet the selected notification criteria.'
  ),
  slotGroup(
    'playlistSlotsNotifications',
    'playlistSlots',
    'playlist-slots',
    'Playlist Slots',
    'You will be notified when any of your playlist slots meet the selected notification criteria.'
  ),
];

export const findNotificationGroup = (slug: string) =>
  NOTIFICATION_GROUPS.find((group) => group.slug === slug);
